<template>
  <div
    id="consult-form"
    class="bg-white py-12 text-slate md:py-[5rem] lg:py-[6rem]"
  >
    <div class="container">
      <div class="mb-12 flex flex-col md:mb-14 md:items-center">
        <div
          class="title mb-6 text-[2rem] uppercase leading-none md:text-[3rem] lg:text-[3.4rem]"
        >
          {{ $t('get_a_consultation') }}
        </div>

        <div
          class="w-[665px] max-w-full text-md leading-[1.8rem] text-slate md:text-center"
        >
          {{ $t('form_consultation_text') }}
        </div>
      </div>

      <client-only>
        <form
          class="mx-auto max-w-[890px]"
          autocomplete="off"
          @submit.prevent="submitForm"
        >
          <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <UiInput
              v-model.trim="v$.first_name.$model"
              :placeholder="$t('name') + '*'"
              :error="errorMessage(v$.first_name)"
              required
            ></UiInput>
            <UiInput
              v-model.trim="v$.last_name.$model"
              :placeholder="$t('last_name') + '*'"
              :error="errorMessage(v$.last_name)"
              required
            ></UiInput>
            <UiInput
              v-model.trim="v$.mobile_phone.$model"
              :placeholder="$t('phone_number') + '*'"
              :error="errorMessage(v$.mobile_phone) && $t('validation_phone')"
              required
              type="tel"
            ></UiInput>
            <UiInput
              v-model.trim="v$.email.$model"
              :placeholder="$t('email') + '*'"
              :error="errorMessage(v$.email)"
              required
              type="email"
            ></UiInput>
            <UiInput
              v-model.trim="v$.company_name.$model"
              :placeholder="$t('company_name') + '*'"
              :error="errorMessage(v$.company_name)"
              required
            ></UiInput>
            <UiSelect
              v-model.trim="v$.country.$model"
              :placeholder="$t('choose_your_country') + '*'"
              :options="countries"
              :error="errorMessage(v$.country)"
              item-id="code"
              item-text="name"
            ></UiSelect>
          </div>

          <UiSelect
            v-model.trim="v$.message_subject.$model"
            :placeholder="$t('choose_email_subject') + '*'"
            :options="messageSubjects"
            :error="errorMessage(v$.message_subject)"
            item-id="id"
            item-text="name"
            class="mb-4"
          ></UiSelect>

          <span class="mb-6 block h-full">
            <UiTextarea
              v-model.trim="v$.business_needs_pain_points_comments.$model"
              :error="errorMessage(v$.business_needs_pain_points_comments)"
              :placeholder="$t('how_can_we_help') + '*'"
              fit-container
              maxlength="2000"
              class="mb-4"
            />
          </span>
          <UiCheckbox
            v-model="formData.marketing_yes"
            class="mb-6 text-sm leading-[1.225rem]"
          >
            {{ $t('i_consent_to_the_processing') }}
          </UiCheckbox>
          <UiCheckbox
            v-model="formData.gdpr_yes"
            class="mb-12 text-sm leading-[1.225rem]"
          >
            {{ $t('personal_data') }}
          </UiCheckbox>

          <UiRecaptcha
            class="mb-6 flex justify-center md:mb-12"
            @error="recaptcha.setToken('')"
            @success="recaptcha.setToken($event)"
            @expired="recaptcha.setToken('')"
          />
          <div class="mx-auto w-full max-w-[438px] justify-center">
            <UiButton
              type="submit"
              size="lg"
              block
              :loading="formPending || recaptcha.isBusy.value"
              :disabled="isSubmitDisabled(v$.$invalid)"
            >
              {{ $t('submit') }}
            </UiButton>
          </div>
        </form>
      </client-only>
    </div>

    <InfoModal v-model="infoModalIsOpen">
      <template #title>
        {{ $t('thank_you') }}
      </template>

      <template #body>
        {{ $t('form_submit_result_success') }}
      </template>

      <template #footer>
        <UiButton size="md" @click="closeInfoModal">
          {{ $t('continue_browsing') }}
        </UiButton>
      </template>
    </InfoModal>
  </div>
</template>

<script setup lang="ts">
import { nextTick, reactive, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';

import {
  useForm,
  useCountries,
  useDomain,
  useGtm,
  useModalDialog,
  useSiteConfigState,
  useValidationRules,
} from '~/composables';
import UiRecaptcha from '~/components/ui/UiRecaptcha.vue';

type TFormPurpose = 'sales' | 'warranty';
type TFormPurposes = Record<TFormPurpose, any>;
interface IMessageSubject {
  id: TFormPurpose;
  name: string;
}
interface ConsultationForm {
  website_url: string;
  lead_source?: string;
  lang: string;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  mobile_phone: string;
  country: string;
  business_needs_pain_points_comments: string;
  message_subject: string;
  marketing_yes: boolean;
  gdpr_yes: boolean;
}

const FORM_DEFAULTS = {
  first_name: '',
  last_name: '',
  mobile_phone: '',
  email: '',
  company_name: '',
  country: '',
  business_needs_pain_points_comments: '',
  message_subject: '',
  marketing_yes: false,
  gdpr_yes: false,
};

const { domain } = useDomain();
const { activeLanguage } = useSiteConfigState();
const site = domain.value as string;
const lang = activeLanguage.value as string;

const { formPending, submit, recaptcha, isSubmitDisabled } = useForm({
  confirm: false,
});

const { t } = useI18n();
const { consultSalesFormUuid, consultWarrantyFormUuid } =
  useRuntimeConfig().public.frontkit;
const { push: pushGtmEvent } = useGtm();
const {
  isOpen: infoModalIsOpen,
  open: openInfoModal,
  close: closeInfoModal,
} = useModalDialog('info');

const formData = reactive<ConsultationForm>({
  website_url: site,
  lang,
  ...FORM_DEFAULTS,
});

const {
  required,
  checked,
  email,
  publicEmail,
  phone,
  alphaSpaces,
  errorMessage,
} = useValidationRules();

const v$ = useVuelidate(
  {
    first_name: { required, alphaSpaces },
    last_name: { required, alphaSpaces },
    mobile_phone: { required, phone },
    email: { required, email, publicEmail },
    company_name: { required, alphaSpaces },
    country: { required },
    message_subject: { required },
    business_needs_pain_points_comments: { required },
    marketing_yes: { checked },
    gdpr_yes: { checked },
  },
  formData,
);

const clearForm = () => {
  Object.assign(formData, {
    website_url: site,
    lang,
    ...FORM_DEFAULTS,
  });

  nextTick(() => v$.value.$reset());
};

const isFormStartEventSent = ref(false);
const { getCountriesList } = useCountries();
const countries = getCountriesList();

const messageSubjects: IMessageSubject[] = [
  { id: 'sales', name: t('consult_subject_sales') },
  { id: 'warranty', name: t('consult_subject_warranty') },
];

const submitForm = async () => {
  try {
    const { message_subject: messageSubject, ...formPayload } = formData;
    const formUuidByPurpose: TFormPurposes = {
      sales: consultSalesFormUuid,
      warranty: consultWarrantyFormUuid,
    };
    const uuid = formUuidByPurpose[messageSubject as TFormPurpose];
    await submit(
      {
        ...formPayload,
        page_url: window.location.href,
        source_description: site,
      },
      uuid,
    );
    pushGtmEvent({
      event: 'leadform_sent',
    });
    openInfoModal();
    clearForm();
  } catch (error) {
    console.error('Error submitting form', error);
  }
};

const pushFormStartEvent = () => {
  if (!isFormStartEventSent.value) {
    pushGtmEvent({
      event: 'form_start',
    });
  }
  isFormStartEventSent.value = true;
};

watch(formData, () => {
  pushFormStartEvent();
});
</script>
